import type {
  LocationData,
  LocationFormData,
  ShipmentFormData,
  VehicleItem,
  VehiclesFormData,
} from '#/features/order-create'
import { TransportType } from '#/shared/api/generated/shipment/models'
import type {
  MarketRateRequest,
  MarketRateRequestTrailerType,
  MarketRateStop,
  MarketRateVehicle,
} from '#/shared/api/generated/loadboard/models'

const VEHICLE_TYPE_TO_CD: Record<string, string> = {
  car: 'CAR',
  suv: 'SUV',
  pickup: 'PICKUP',
  van: 'VAN',
  motorcycle: 'MOTORCYCLE',
  rv: 'RV',
  travel: 'TRAILER',
  trailer: 'TRAILER',
  boat: 'OTHER',
  atv: 'OTHER',
  heavy: 'OTHER',
  large: 'OTHER',
  other: 'OTHER',
}

const TRAILER_TO_CD: Partial<Record<string, MarketRateRequestTrailerType>> = {
  [TransportType.OPEN]: 'Open',
  [TransportType.ENCLOSED]: 'Enclosed',
  [TransportType.DRIVE_AWAY]: 'Drive-Away',
}

function trim(raw: string | undefined): string {
  return (raw ?? '').trim()
}

function locationToStop(loc: LocationData): MarketRateStop | undefined {
  const city = trim(loc.city)
  const state = trim(loc.state)
  if (!city || state.length !== 2) return undefined
  const stop: MarketRateStop = { city, state: state.toUpperCase() }
  const zip = trim(loc.zip)
  if (zip) stop.zip_code = zip
  const address = trim(loc.address)
  if (address) stop.address = address
  const businessName = trim(loc.businessName)
  if (businessName) stop.business_name = businessName
  return stop
}

function vehicleToCd(item: VehicleItem): MarketRateVehicle | undefined {
  const cdType = VEHICLE_TYPE_TO_CD[item.type.toLowerCase()] ?? 'OTHER'
  const year = Number.parseInt(item.year, 10)
  const make = trim(item.make)
  const model = trim(item.model)
  const vehicle: MarketRateVehicle = { type: cdType }
  if (Number.isFinite(year)) vehicle.year = year
  if (make) vehicle.make = make
  if (model) vehicle.model = model
  if (item.inop) vehicle.is_operable = false
  const vin = trim(item.vin)
  if (vin) vehicle.vin = vin
  return vehicle
}

export class LaneMarketRateValidationError extends Error {
  constructor(public readonly reason: 'origin' | 'destination' | 'vehicles') {
    super(`Lane market rate request invalid: ${reason}`)
  }
}

export function toLaneMarketRateRequest({
  location,
  vehicles,
  shipment,
}: {
  location: LocationFormData
  vehicles: VehiclesFormData
  shipment: ShipmentFormData
}): MarketRateRequest {
  const origin = locationToStop(location.origin)
  if (!origin) throw new LaneMarketRateValidationError('origin')
  const destination = locationToStop(location.destination)
  if (!destination) throw new LaneMarketRateValidationError('destination')
  const cdVehicles = vehicles.vehicles
    .map(vehicleToCd)
    .filter((v): v is MarketRateVehicle => v !== undefined)
  if (cdVehicles.length === 0) {
    throw new LaneMarketRateValidationError('vehicles')
  }
  const trailer = TRAILER_TO_CD[shipment.trailer]
  return {
    origin,
    destination,
    vehicles: cdVehicles,
    ...(trailer ? { trailer_type: trailer } : {}),
  }
}
